// crewbit-admin/src/hooks/useConfirmProposal.ts
//
// Tier 2 Phase B §WorldClass.3 — tax automation substrate.
// Admin paired hook for confirm_expense_proposal RPC (Migration 273).
// Mirrors crewbit-mobile/hooks/useConfirmProposal.ts.
//
// Web runtime: window.dispatchEvent(MONEY_MUTATION_EVENT) on success.

import { useCallback } from 'react';
import { callMoneyRpc, type MoneyRpcResult } from '../lib/moneyRpc';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';
import type {
  ConfirmProposalAction,
  ConfirmProposalOverrides,
  CcaClass,
} from '../types/proposals';

export interface ConfirmProposalArgs {
  proposalId: string;
  action: ConfirmProposalAction;
  overrides?: ConfirmProposalOverrides;
  idempotencyKey?: string | null;
}

export type ConfirmProposalHookResult = MoneyRpcResult<{
  proposal_id?: string;
  action?: 'declined';
  expense_id?: string;
  ledger_id?: string;
  t2125_line?: string | null;
  is_capital_purchase?: boolean;
  cca_class?: CcaClass | null;
  already_created?: boolean;
}>;

export function useConfirmProposal() {
  return useCallback(
    async (args: ConfirmProposalArgs): Promise<ConfirmProposalHookResult> => {
      const result = await callMoneyRpc<{
        proposal_id?: string;
        action?: 'declined';
        expense_id?: string;
        ledger_id?: string;
        t2125_line?: string | null;
        is_capital_purchase?: boolean;
        cca_class?: CcaClass | null;
        already_created?: boolean;
      }>('confirm_expense_proposal', {
        p_proposal_id: args.proposalId,
        p_action: args.action,
        p_overrides: args.overrides ?? null,
        p_idempotency_key: args.idempotencyKey ?? null,
      });
      if (result.success) {
        window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
      }
      return result;
    },
    [],
  );
}
